/** The grid regions the factor library can price electricity for — `GET /api/v1/grid-regions`.
 *
 * **The list is the server's, never a second one here.** The picker offers exactly what the server's
 * refusal of an unpriced region accepts (both are `engine_sets.grid_regions`), so a region cannot be
 * offered that a save then rejects. A region carries a code, the verification-log row behind its
 * factor, and the publisher's own name and plant states where the server has them (EPA's eGRID
 * workbook, row C-10d). A name is never typed here: it is the server's, and it would be a fact
 * without a source if it were (rule 2).
 *
 * Reference data, so it is cached for the session: the set is locked and a correction is a new
 * edition, which a reload picks up.
 */

import { useQuery } from "@tanstack/react-query";

import { api } from "./client";
import { refusalFrom } from "./refusal";
import type { components } from "./schema";

export type GridRegions = components["schemas"]["FactorLibraryGridRegionsOut"];
export type GridRegion = components["schemas"]["FactorLibraryGridRegionOut"];
export type GridRegionJurisdiction = GridRegions["jurisdiction"];

/** The jurisdiction values the API accepts, **from the generated schema** rather than restated. */
export type GridRegionsQuery = components["schemas"]["Jurisdiction"];

export function useGridRegions(jurisdiction: GridRegionsQuery) {
  return useQuery({
    queryKey: ["grid-regions", jurisdiction] as const,
    retry: false,
    staleTime: Infinity,
    queryFn: async (): Promise<GridRegions> => {
      const { data, error, response } = await api.GET("/api/v1/grid-regions", {
        params: { query: { jurisdiction } },
      });
      if (error || !data) {
        throw refusalFrom(response, error, "The grid regions could not be read.");
      }
      return data;
    },
  });
}
